import { api } from "../api";
import { S, isAdmin, isStaff, on, render } from "../state";
import { $, I, avatar, edHead, esc, fullName, openSheet, closeSheet, sized, toast, wirePhotos, pinFor } from "../ui";
import { EVENT_TYPES, addDays, fmtDay, fmtTime, isTour, nowLocal, parseDay, typeInfo } from "../../shared/constants";
import type { EventView, SignupView } from "../../shared/types";
import { E, allEvents, colorOf, ensureYear, leads, mySignup, put, shortTitle, sortEv, stateOf } from "../events";
import { openEditor } from "./editor";

export const C: { view: "month" | "list"; year: number; month: number; sel: string; inited: boolean } = {
  view: "month",
  year: 0,
  month: 0,
  sel: "",
  inited: false,
};

const ICONS = {
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5 9-10"/></svg>',
  lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
};

export function initCal() {
  if (C.inited) return;
  const t = S.me!.today;
  C.year = Number(t.slice(0, 4));
  C.month = Number(t.slice(5, 7)) - 1;
  C.sel = t;
  C.view = S.me!.user.defaultView;
  C.inited = true;
}

const today = () => S.me!.today;
const floor = () => `${S.me!.schoolYear}-07-01`;
const monthKey = () => `${C.year}-${String(C.month + 1).padStart(2, "0")}`;

/** An event for this ambassador's group: their role has spots on it, it's a Team Event, or they're on it. */
const forMyGroup = (e: EventView) => {
  const r = S.me!.user.roles;
  return e.type === "team" || !!mySignup(e) || (r.pa && (e.pa > 0 || e.paShadow)) || (r.sa && (e.sa > 0 || e.saShadow));
};

/**
 * The earliest month an ambassador can go back to: the first month of this school year with an event
 * for their group, and never later than this month. So a school year doesn't open on empty months,
 * but empty months after the first event still show (Peter, 2026-11-04). Staff have no limit.
 */
const floorMonth = () => {
  const now = today().slice(0, 7);
  const first = allEvents()
    .filter((e) => e.date >= floor() && e.date.slice(0, 7) < now && forMyGroup(e))
    .reduce((m, e) => (e.date < m ? e.date : m), "9999");
  return first < now ? first.slice(0, 7) : now;
};

/** The latest month an ambassador can go forward to: the last month with an upcoming event for their group, never earlier than this month. */
const ceilMonth = () => {
  const now = today().slice(0, 7);
  const last = allEvents()
    .filter((e) => e.date >= today() && forMyGroup(e))
    .reduce((m, e) => (e.date > m ? e.date : m), "");
  return last.slice(0, 7) > now ? last.slice(0, 7) : now;
};
const typesOff = () => new Set(S.me!.user.filtersOff);

/** Ambassadors see events that already ended faded, like full ones; Admin and Admissions don't (Peter, 2026-11-04). */
function fadedPast(e: EventView) {
  if (isStaff()) return false;
  const n = nowLocal();
  return e.date < n.day || (e.date === n.day && e.end <= n.time);
}

function icon(st: ReturnType<typeof stateOf>) {
  const m = {
    mine: ["mine", ICONS.check, "You're signed up"],
    open: ["open", I.plus, "Spots open"],
    full: ["full", ICONS.lock, "Full"],
    cx: ["cx", I.x, "Canceled"],
    team: ["open", I.user, "Team Event"],
  }[st];
  return `<span class="ico ${m[0]}" title="${m[2]}" aria-label="${m[2]}">${m[1]}</span>`;
}

/** Events the calendar shows after filters. Team Events always show. */
function calEvents() {
  const off = typesOff();
  const hideFull = S.me!.user.hideFull && (S.me!.user.roles.pa || S.me!.user.roles.sa);
  const fm = isStaff() ? "" : floorMonth();
  return allEvents().filter((e) => {
    if (e.date.slice(0, 7) < fm) return false;
    if (e.type === "team") return true;
    if (off.has(e.type)) return false;
    if (hideFull) return ["open", "mine"].includes(stateOf(e));
    return true;
  });
}

/**
 * ✓ when someone confirmed or accepted the invite. The server only sends these fields to Admissions and
 * admins (for everyone) and to each ambassador for their own sign-up (Peter, 2026-11-04).
 */
const statusText = (s: SignupView) =>
  s.confirmed
    ? '<span class="stat conf" title="Attendance confirmed">✓</span>'
    : s.invite === "accepted"
      ? '<span class="stat acc" title="Invite accepted">✓</span>'
      : "";

export function erow(e: EventView) {
  const st = stateOf(e);
  const mine = mySignup(e);
  const tag =
    e.status === "draft"
      ? '<span class="chip">Draft</span>'
      : e.status === "canceled"
        ? '<span class="chip red">Canceled</span>'
        : mine
          ? `<span class="chip blue">You: ${mine.kind}${mine.shadow ? " (Shadow)" : ""}</span>`
          : "";
  const who = (lab: string, cap: number, kind: "PA" | "SA") => {
    const list = e.signups.filter((s) => s.kind === kind);
    if (!cap && !list.length) return "";
    const n = leads(e, kind).length;
    const names = list.map((s) => esc(fullName(s)) + (s.shadow ? " (Shadow)" : "") + statusText(s));
    const open = cap - n;
    return `<div><b>${lab}${cap > 1 ? "s" : ""}:</b> ${names.length ? names.join(", ") : "None"}${open > 0 && e.status !== "canceled" ? ` <span class="muted">(${names.length ? "+" : ""}${open} open)</span>` : ""}</div>`;
  };
  const spots = e.type === "team" ? '<span class="muted">Ambassador Team Event</span>' : `<div class="small" style="line-height:1.5">${who("PA", e.pa, "PA")}${who("SA", e.sa, "SA")}</div>`;
  const d = parseDay(e.date);
  return `<button class="erow ${st === "mine" ? "mine" : ""} ${fadedPast(e) ? "past" : ""}" style="--c:${colorOf(e)};${e.status === "draft" ? "font-style:italic;opacity:.7" : ""}" data-act="open-event" data-id="${e.id}">
    <div class="when"><span>${d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" })}</span><b>${d.getUTCDate()}</b><span style="color:var(--muted)">${d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}</span></div>
    <div class="body"><div class="row between"><b>${esc(e.title)}</b>${icon(st)}</div>
    <div class="muted small">${fmtTime(e.start)} to ${fmtTime(e.end)}</div>
    ${tag ? `<div class="row wrap small" style="margin-top:6px;gap:6px">${tag}</div>` : ""}<div style="margin-top:6px">${spots}</div></div></button>`;
}

export function calendar() {
  initCal();
  void ensureYear(`${monthKey()}-01`).then((fresh) => fresh && render(), (e) => toast((e as Error).message));
  // Ambassadors also load next school year, so events after June 30 count toward how far ahead they can go.
  if (!isStaff()) void ensureYear(`${S.me!.schoolYear + 1}-07-01`).then((fresh) => fresh && render(), (e) => toast((e as Error).message));
  // Ambassadors only page through months between their group's first and last events (Peter, 2026-11-04).
  const fm = isStaff() ? "" : floorMonth();
  const cm = isStaff() ? "" : ceilMonth();
  const clampTo = (k: string) => ([C.year, C.month] = [Number(k.slice(0, 4)), Number(k.slice(5, 7)) - 1]);
  if (fm && monthKey() < fm) clampTo(fm);
  if (cm && monthKey() > cm) clampTo(cm);
  const atFloor = !!fm && monthKey() <= fm;
  const atCeil = !!cm && monthKey() >= cm;
  const ev = calEvents();
  const y = C.year, m = C.month;
  const first = new Date(Date.UTC(y, m, 1, 12));
  const title = first.toLocaleDateString("en-US", { month: innerWidth < 480 ? "short" : "long", year: "numeric", timeZone: "UTC" });
  let grid = "";
  if (C.view === "month") {
    const inMonth = ev.filter((e) => e.date.startsWith(monthKey()));
    // Weekends only show when this month has a weekend event.
    const wk = inMonth.some((e) => [0, 6].includes(parseDay(e.date).getUTCDay()));
    const startDow = first.getUTCDay();
    const days = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const weeks = Math.ceil((startDow + days) / 7);
    const dows = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    grid = `<div class="cal" style="grid-template-columns:repeat(${wk ? 7 : 5},1fr)">` + dows.filter((_, i) => wk || (i > 0 && i < 6)).map((d) => `<div class="dow">${d}</div>`).join("");
    const start = addDays(`${monthKey()}-01`, -startDow);
    for (let i = 0; i < weeks * 7; i++) {
      if (!wk && (i % 7 === 0 || i % 7 === 6)) continue;
      const k = addDays(start, i);
      const es = ev.filter((e) => e.date === k).sort(sortEv);
      grid += `<button class="d ${k.slice(0, 7) !== monthKey() ? "out" : ""} ${k === today() ? "today" : ""} ${k === C.sel ? "sel" : ""}" data-act="sel-day" data-day="${k}" aria-label="${fmtDay(k)}, ${es.length} events"><span class="n">${Number(k.slice(8))}</span>${isStaff() && k >= today() ? `<span class="dadd" role="button" tabindex="0" data-act="add-on-day" data-day="${k}" aria-label="Add an event on ${fmtDay(k)}" title="Add an event">${sized(I.plus, 10)}</span>` : ""}<span class="evs">${es
        .map((e) => {
          const st = stateOf(e);
          return `<span style="--c:${colorOf(e)}" data-act="pill" data-id="${e.id}" data-day="${k}" class="ev ${st === "mine" ? "mine" : st === "full" ? "full" : ""} ${fadedPast(e) ? "past" : ""} ${e.status === "draft" ? "draft" : ""}">${st === "mine" ? "✓ " : ""}${fmtTime(e.start).replace(":00", "").replace(" AM", "a").replace(" PM", "p")} ${esc(shortTitle(e.title))}</span>`;
        })
        .join("")}</span>${es.length > 4 ? `<span class="more">+${es.length - 4} more</span>` : ""}</button>`;
    }
    grid += "</div>";
    const dayEv = ev.filter((e) => e.date === C.sel).sort(sortEv);
    const allDays = [...new Set(ev.map((e) => e.date))].sort();
    const hasPrev = allDays.some((x) => x < C.sel), hasNext = allDays.some((x) => x > C.sel);
    grid += `<div style="margin-top:16px;scroll-margin-top:8px" id="agenda"><div class="row between" style="margin-bottom:10px"><h2>${fmtDay(C.sel)}</h2><div class="row" style="gap:6px"><button class="btn ghost sm" style="font-size:22px;width:44px;line-height:1" aria-label="Previous day with events" ${hasPrev ? "" : "disabled"} data-act="step-day" data-dir="-1">‹</button><button class="btn ghost sm" style="font-size:22px;width:44px;line-height:1" aria-label="Next day with events" ${hasNext ? "" : "disabled"} data-act="step-day" data-dir="1">›</button></div></div><div class="stack agenda">${dayEv.map(erow).join("") || '<p class="muted">No events this day.</p>'}</div></div>`;
  } else {
    const list = ev.filter((e) => e.date >= today()).sort(sortEv);
    const days = [...new Set(list.map((e) => e.date))];
    grid =
      days
        .map((k) => `<div class="dayhead">${fmtDay(k, { weekday: "long", month: "short", day: "numeric" })}</div><div class="stack agenda">${list.filter((e) => e.date === k).map(erow).join("")}</div>`)
        .join("") || '<p class="muted">No upcoming events match these filters.</p>';
  }
  // Chips only for types that happen in the month on screen; hidden types stay on.
  const have = new Set(allEvents().filter((e) => e.date.startsWith(monthKey())).map((e) => e.type));
  const chips = [...EVENT_TYPES].filter((t) => t.key !== "team" && have.has(t.key)).sort((a, b) => a.label.localeCompare(b.label));
  const off = typesOff();
  const amb = S.me!.user.roles.pa || S.me!.user.roles.sa;
  return `<div class="stack">
    <div class="row between" style="gap:8px">
      ${C.view === "month" ? `<div class="row" style="gap:4px;min-width:0"><button class="btn ghost sm" style="font-size:20px;width:40px;line-height:1" aria-label="Previous month" ${atFloor ? "disabled" : ""} data-act="move-month" data-dir="-1">‹</button><button class="btn ghost sm" style="font-size:20px;width:40px;line-height:1" aria-label="Next month" ${atCeil ? "disabled" : ""} data-act="move-month" data-dir="1">›</button><h2 style="white-space:nowrap;font-size:18px;margin-left:6px">${title}</h2></div>` : "<span></span>"}
      <div class="row" style="gap:6px">
        ${isStaff() ? `<button class="btn sm" aria-label="Add event" data-act="new-event">${sized(I.plus, 14)}<span class="hide-sm">Add</span></button>` : ""}
        <div class="seg" role="tablist"><button class="${C.view === "month" ? "on" : ""}" data-act="cal-view" data-v="month">Month</button><button class="${C.view === "list" ? "on" : ""}" data-act="cal-view" data-v="list">List</button></div>
      </div></div>
    <div class="row wrap small muted" style="gap:8px">
      ${chips.map((t) => `<button class="fchip ${off.has(t.key) ? "" : "on"}" aria-pressed="${!off.has(t.key)}" data-act="toggle-type" data-k="${t.key}"><span class="swatch" style="--c:${t.color}"></span>${t.label}</button>`).join("")}
      ${have.has("team") ? `<span class="fchip on" style="cursor:default;border-style:dashed" title="Team Events always show"><span class="swatch" style="--c:${typeInfo("team").color}"></span>Team Events</span>` : ""}
      ${amb ? `<button class="fchip ${S.me!.user.hideFull ? "on" : ""}" aria-pressed="${S.me!.user.hideFull}" data-act="toggle-full">${S.me!.user.hideFull ? "✓ " : ""}Hide Fully Booked Events</button>` : ""}
    </div>
    ${grid}
  </div>`;
}

async function saveFilters(body: Record<string, unknown>) {
  try {
    S.me = await api("/api/me", { method: "PATCH", body });
  } catch (e) {
    toast((e as Error).message);
  }
  render();
}

function moveMonth(d: number) {
  C.month += d;
  if (C.month < 0) { C.month = 11; C.year--; }
  if (C.month > 11) { C.month = 0; C.year++; }
  const pre = monthKey();
  const days = calEvents().filter((e) => e.date.startsWith(pre)).map((e) => e.date).sort();
  C.sel = today().startsWith(pre) ? today() : days.find((x) => x >= today()) || days[0] || `${pre}-01`;
  render();
}

/* ---------------- event pop-up ---------------- */

function canAdd(e: EventView, kind: "PA" | "SA") {
  if (isStaff()) return e.status !== "canceled" && e.type !== "team";
  const r = S.me!.user.roles;
  return (kind === "PA" ? r.pa : r.sa) && !mySignup(e) && e.status === "published" && e.date >= today() && !S.me!.maintenance;
}

function person(e: EventView, s: SignupView, live: boolean) {
  const mine = s.userId === S.me!.user.id;
  const line2 = [isStaff() && s.phone ? esc(s.phone) : "", s.titles.length ? s.titles.map(esc).join(", ") : ""].filter(Boolean).join(" • ");
  const act = !live
    ? ""
    : mine
      ? `<button class="btn ghost sm" style="color:var(--accent)" data-act="ask-leave" data-id="${e.id}">Remove Me</button>`
      : isStaff()
        ? `<button class="btn ghost sm" aria-label="Remove ${esc(fullName(s))}" data-act="staff-remove" data-id="${e.id}" data-sid="${s.id}" data-name="${esc(fullName(s))}">Remove</button>`
        : "";
  return `<li class="prow ${mine ? "me" : ""}" ${mine ? 'id="merow"' : ""}><div class="row" style="gap:12px">${s.userId ? avatar(s.userId) : avatar(0)}<div><b>${esc(fullName(s))}</b>${statusText(s)}${s.shadow ? ' <span class="chip">Shadow</span>' : ""}${mine ? ' <span class="chip blue">You</span>' : ""}${line2 ? `<div class="muted small">${line2}</div>` : ""}</div>${pinFor(s.userId, 38)}</div>${act}</li>`;
}

function group(e: EventView, label: string, kind: "PA" | "SA", cap: number, shadowOk: boolean) {
  const regs = e.signups.filter((s) => s.kind === kind && !s.shadow);
  const shs = e.signups.filter((s) => s.kind === kind && s.shadow);
  if (!cap && !shadowOk && !regs.length && !shs.length) return "";
  const live = e.status !== "canceled" && e.status !== "completed";
  const staff = isStaff();
  const tour = isTour(e.title);
  const openRow = (shadow: boolean, first: boolean) => {
    const placeholder = shadow ? "Shadowing allowed" : tour ? "Open Tour Lead spot" : "Open spot";
    if (!canAdd(e, kind)) return `<li class="prow open"><span class="muted">${placeholder}</span></li>`;
    const cta = staff ? (shadow ? "Add Shadow" : tour ? "Add Tour Lead" : "Add") : shadow ? (tour ? "Shadow This Tour" : "Join as Shadow") : tour ? "Join as Tour Lead" : "Join";
    const act = staff ? `data-act="staff-add" data-id="${e.id}" data-kind="${kind}" data-shadow="${shadow ? 1 : 0}"` : `data-act="join" data-id="${e.id}" data-kind="${kind}" data-shadow="${shadow ? 1 : 0}"`;
    return first
      ? `<li class="prow open" style="padding:0"><button class="joinbtn" ${act}><span class="plus">${I.plus}</span><span>${cta}</span></button></li>`
      : `<li class="prow open" style="padding:0"><button class="joinbtn off" disabled aria-hidden="true" tabindex="-1"><span class="plus">${I.plus}</span><span>${cta}</span></button></li>`;
  };
  let rows = regs.map((s) => person(e, s, live)).join("");
  if (live) for (let i = regs.length; i < Math.min(cap, regs.length + 2); i++) rows += openRow(false, i === regs.length);
  if (live && cap - regs.length > 2) rows += `<li class="prow open"><span class="muted">+ ${cap - regs.length - 2} More Open Spots</span></li>`;
  rows += shs.map((s) => person(e, s, live)).join("");
  if (live && shadowOk) rows += openRow(true, true);
  return `<h3 style="margin:16px 0 4px">${label}${cap > 1 ? "s" : ""}</h3><ul class="list plist">${rows || '<li class="muted">None</li>'}</ul>`;
}

export function openEvent(id: number) {
  const e = E.byId.get(id);
  if (!e) return;
  let top = "";
  if (e.status === "canceled") top = `<div class="note">Admissions canceled this event.</div>`;
  else if (S.me!.maintenance && !isAdmin() && e.type !== "team") top = `<div class="note">Sign-ups are paused during maintenance.</div>`;
  let staff = "";
  if (isStaff() && e.status !== "canceled") {
    staff = `<div class="row wrap" style="margin-top:18px;padding-top:14px;border-top:1px solid var(--line)">
      ${e.status === "draft" ? `<button class="btn sm" data-act="publish" data-id="${id}" data-notify="1">Publish &amp; Notify</button><button class="btn ghost sm" data-act="publish" data-id="${id}" data-notify="0">Publish</button>` : ""}
      ${e.type !== "team" && e.status !== "draft" && (e.date === today() || (e.date < today() && isAdmin())) ? `<button class="btn ghost sm" data-act="go-checkin" data-day="${e.date}">Check-in</button>` : ""}
      <button class="btn ghost sm" data-act="edit-event" data-id="${id}">Edit</button><button class="btn ghost sm" data-act="dup-event" data-id="${id}">Duplicate</button>
      <span id="cxwrap"><button class="btn ghost sm" style="color:var(--accent)" data-act="ask-cancel" data-id="${id}">Cancel Event</button></span></div>`;
  }
  const body = e.type === "team" ? "" : group(e, "Parent Ambassador", "PA", e.pa, e.paShadow) + group(e, "Student Ambassador", "SA", e.sa, e.saShadow);
  const chip = e.type !== "team" && e.status !== "published" ? `<span class="chip ${e.status === "canceled" ? "red" : ""}" style="align-self:flex-start;margin-bottom:8px">${e.status[0].toUpperCase() + e.status.slice(1)}</span>` : "";
  openSheet(`${edHead(esc(e.title))}
    <div class="edbody" style="gap:0">${chip}
    <p style="margin:0 0 12px"><b>${fmtDay(e.date, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</b><br><span class="muted">${fmtTime(e.start)} to ${fmtTime(e.end)}</span></p>
    ${top}${e.notes ? `<div class="note" style="background:var(--surface2);color:var(--text)"><b>Notes</b><br>${esc(e.notes).replace(/\n/g, "<br>")}</div>` : ""}${body}${staff}</div>`);
  wirePhotos($("#sheet")!);
}

async function act(path: string, body: unknown, msg: string, method = "POST") {
  try {
    const e = put(await api<EventView>(path, { method, body }));
    render();
    openEvent(e.id);
    toast(msg);
  } catch (err) {
    toast((err as Error).message);
  }
}

let people: { id: number; first: string; last: string; pa: boolean; sa: boolean }[] | null = null;

on({
  "open-event": (el) => openEvent(Number(el.dataset.id)),
  "sel-day": (el) => {
    C.sel = el.dataset.day!;
    render();
    if (innerWidth < 700) document.getElementById("agenda")?.scrollIntoView({ behavior: "smooth", block: "start" });
  },
  pill: (el, ev) => {
    if (innerWidth <= 600) return void (C.sel = el.dataset.day!, render());
    ev.stopPropagation();
    C.sel = el.dataset.day!;
    render();
    openEvent(Number(el.dataset.id));
  },
  "step-day": (el) => {
    const days = [...new Set(calEvents().map((e) => e.date))].sort();
    const k = Number(el.dataset.dir) > 0 ? days.find((x) => x > C.sel) : days.filter((x) => x < C.sel).pop();
    if (!k) return;
    C.sel = k;
    C.year = Number(k.slice(0, 4));
    C.month = Number(k.slice(5, 7)) - 1;
    render();
  },
  "move-month": (el) => moveMonth(Number(el.dataset.dir)),
  "cal-view": (el) => {
    C.view = el.dataset.v as "month" | "list";
    render();
  },
  "toggle-type": (el) => {
    const k = el.dataset.k!;
    const off = typesOff();
    if (off.has(k)) off.delete(k);
    else off.add(k);
    void saveFilters({ filtersOff: [...off] });
  },
  "toggle-full": () => void saveFilters({ hideFull: !S.me!.user.hideFull }),
  join: (el) =>
    act(`/api/events/${el.dataset.id}/join`, { kind: el.dataset.kind, shadow: el.dataset.shadow === "1" }, el.dataset.shadow === "1" ? "You're shadowing this event." : "You're in!"),
  "ask-leave": (el) => {
    const r = $("#merow")!;
    r.outerHTML = `<li class="prow me" style="display:block"><b>Remove yourself from this event?</b><p class="muted small" style="margin:4px 0 10px">Your spot opens up and other ambassadors are alerted if the event is soon.</p><div class="row"><button class="btn danger sm" data-act="leave" data-id="${el.dataset.id}">Yes, Remove Me</button><button class="btn ghost sm" data-act="open-event" data-id="${el.dataset.id}">Keep My Spot</button></div></li>`;
  },
  leave: (el) => act(`/api/events/${el.dataset.id}/leave`, undefined, "You've been removed from this event.", "POST"),
  "staff-remove": (el) => act(`/api/events/${el.dataset.id}/signups/${el.dataset.sid}`, undefined, `${el.dataset.name} removed`, "DELETE"),
  "staff-add": async (el) => {
    const li = el.closest("li")!;
    const kind = el.dataset.kind!;
    li.style.display = "block";
    li.style.padding = "8px 0";
    li.innerHTML = `<div class="row" style="gap:6px"><input type="text" id="saq" placeholder="Type a ${kind === "SA" ? "Student" : "Parent"} Ambassador's name" autocomplete="off" data-input="staff-search" data-id="${el.dataset.id}" data-kind="${kind}" data-shadow="${el.dataset.shadow}"><button class="btn ghost sm" data-act="open-event" data-id="${el.dataset.id}">Cancel</button></div><ul class="list wkres" id="sar"></ul>`;
    ($("#saq") as HTMLInputElement).focus();
    if (!people) people = await api("/api/events/people").catch(() => []);
  },
  "staff-search": (el) => {
    const q = (el as HTMLInputElement).value.trim().toLowerCase();
    const e = E.byId.get(Number(el.dataset.id))!;
    const kind = el.dataset.kind!;
    const taken = new Set(e.signups.map((s) => s.userId));
    const hits = q
      ? (people || [])
          .filter((p) => (kind === "SA" ? p.sa : p.pa) && !taken.has(p.id) && `${p.first} ${p.last}`.toLowerCase().split(" ").concat(`${p.first} ${p.last}`.toLowerCase()).some((w) => w.startsWith(q)))
          .slice(0, 8)
      : [];
    $("#sar")!.innerHTML =
      hits.map((p) => `<li><button class="wkhit" data-act="staff-pick" data-id="${e.id}" data-uid="${p.id}" data-kind="${kind}" data-shadow="${el.dataset.shadow}" data-name="${esc(fullName(p))}"><b>${esc(fullName(p))}</b><span class="chip ${kind === "SA" ? "red" : "blue"}">${kind}</span></button></li>`).join("") ||
      (q ? '<li class="muted small" style="padding:8px 0">No matches</li>' : "");
  },
  "staff-pick": (el) =>
    act(`/api/events/${el.dataset.id}/signups`, { userId: Number(el.dataset.uid), kind: el.dataset.kind, shadow: el.dataset.shadow === "1" }, `${el.dataset.name} added`),
  "ask-cancel": (el) => {
    $("#cxwrap")!.innerHTML = `<span class="row wrap" style="gap:6px"><span class="small"><b>Cancel for everyone?</b></span><button class="btn danger sm" data-act="cancel-event" data-id="${el.dataset.id}">Yes, Cancel</button><button class="btn ghost sm" data-act="open-event" data-id="${el.dataset.id}">No</button></span>`;
  },
  "cancel-event": async (el) => {
    try {
      put(await api<EventView>(`/api/events/${el.dataset.id}/cancel`, { method: "POST" }));
      closeSheet();
      render();
      toast("Event canceled");
    } catch (err) {
      toast((err as Error).message);
    }
  },
  publish: async (el) => {
    const notify = el.dataset.notify === "1";
    try {
      put(await api<EventView>(`/api/events/${el.dataset.id}/publish`, { body: { notify } }));
      closeSheet();
      render();
      toast(notify ? "Published. Matching ambassadors get one notification 10 minutes after your last Publish & Notify." : "Published. No notification sent.");
    } catch (err) {
      toast((err as Error).message);
    }
  },
  "new-event": () => openEditor(null),
  "add-on-day": (el, ev) => {
    ev.stopPropagation();
    void openEditor(null, false, false, el.dataset.day);
  },
  "edit-event": (el) => openEditor(Number(el.dataset.id), false, true),
  "dup-event": (el) => openEditor(Number(el.dataset.id), true, true),
});

export function resetPeopleCache() {
  people = null;
}

/** Opens an event's pop-up from a link (notifications use #calendar?event=ID), on that event's month. */
export async function openEventById(id: number) {
  try {
    const e = put(await api<EventView>(`/api/events/${id}`));
    initCal();
    C.year = Number(e.date.slice(0, 4));
    C.month = Number(e.date.slice(5, 7)) - 1;
    C.sel = e.date;
    history.replaceState(null, "", "#calendar");
    render();
    openEvent(id);
  } catch (err) {
    toast((err as Error).message);
  }
}
